import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
} from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
  GetSubscriptionDocument,
  GetPlansListDocument,
  type GetSubscriptionQuery,
  type GetPlansListQuery,
} from '@/generated/root-types';
import { subscriptionStateColors, translateSubscriptionState } from '@/utils/billingStateLabels';
import { setOverrideTenantId } from '@gastrosoft/shared/utils';
import { GRAPHQL_MAX_PAGE_SIZE } from '@/utils/constants';
import { ResponsiveRowTableCards, type ResponsiveRowColumn } from '@gastrosoft/shared/ui';
import {
  detailBackButtonSx,
  detailHeaderRowWrapSx,
  detailPageContainerSx,
  detailTitleSx,
} from '@/utils/detailPageLayout';

type ItemNode = NonNullable<
  NonNullable<GetSubscriptionQuery['subscription']>['subscriptionItems']
>[number];

export default function SubscriptionDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { fromTenantId, fromLicenceId } = (location.state ?? {}) as {
    fromTenantId?: string;
    fromLicenceId?: string;
  };

  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState<ItemNode | null>(null);
  const [planId, setPlanId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [unitNet, setUnitNet] = useState('');
  const [unitGross, setUnitGross] = useState('');
  const [currencyCode, setCurrencyCode] = useState('EUR');

  const locale = i18n.language === 'de' ? 'de-DE' : 'en-US';
  const formatCurrency = useCallback(
    (amount?: number | null, currency?: string | null) =>
      amount != null
        ? new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currency && currency.length === 3 ? currency : 'EUR',
          }).format(amount)
        : '–',
    [locale],
  );
  const formatDate = (date?: string | null) =>
    date ? new Date(date).toLocaleDateString(locale) : '–';

  const { data, loading, error } = useQuery<GetSubscriptionQuery>(GetSubscriptionDocument, {
    variables: { id },
    skip: !id,
  });

  const sub = data?.subscription;

  useEffect(() => {
    if (sub?.tenantId) setOverrideTenantId(sub.tenantId);
    else setOverrideTenantId(null);
    return () => setOverrideTenantId(null);
  }, [sub?.tenantId]);

  const { data: plansData } = useQuery<GetPlansListQuery>(GetPlansListDocument, {
    variables: { first: GRAPHQL_MAX_PAGE_SIZE },
  });
  const planOptions = plansData?.plans?.nodes ?? [];

  const handleBack = () => {
    if (fromLicenceId) navigate(`/licences/${fromLicenceId}`);
    else if (fromTenantId) navigate(`/customers/${fromTenantId}`);
    else navigate('/subscriptions');
  };

  const openAdd = () => {
    setPlanId(planOptions[0]?.id ?? '');
    setQuantity('1');
    setUnitNet('');
    setUnitGross('');
    setCurrencyCode(sub?.currencyCode ?? 'EUR');
    setAddOpen(true);
  };

  const openEdit = useCallback(
    (item: ItemNode) => {
      setEditItem(item);
      setQuantity(String(item.quantity));
      setUnitNet(String(item.unitPriceNet));
      setUnitGross(String(item.unitPriceGross));
      setCurrencyCode(sub?.currencyCode ?? 'EUR');
    },
    [sub?.currencyCode],
  );

  const itemColumns = useMemo((): ResponsiveRowColumn<ItemNode>[] => {
    const cc = sub?.currencyCode;
    return [
      {
        id: 'qty',
        header: t('common.quantity'),
        render: (item) => <Typography variant="body2">{item.quantity}</Typography>,
      },
      {
        id: 'net',
        header: t('common.net'),
        render: (item) => (
          <Typography variant="body2">{formatCurrency(item.unitPriceNet ?? cc)}</Typography>
        ),
      },
      {
        id: 'gross',
        header: t('common.gross'),
        render: (item) => (
          <Typography variant="body2">{formatCurrency(item.unitPriceGross ?? cc)}</Typography>
        ),
      },
      {
        id: 'actions',
        header: t('common.actions'),
        align: 'right',
        render: (item) => (
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              flexWrap: 'wrap',
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
            }}
          >
            <IconButton size="small" aria-label={t('common.edit')} onClick={() => openEdit(item)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" aria-label={t('common.delete')}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
    ];
  }, [t, sub?.currencyCode, formatCurrency, openEdit]);

  if (loading) {
    return (
      <Box
        sx={{
          ...detailPageContainerSx,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !sub) {
    return (
      <Box sx={detailPageContainerSx}>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={{ mb: 2 }}>
          {t('common.back')}
        </Button>
        <Typography color="error">{error?.message ?? t('errors.subscriptionNotFound')}</Typography>
      </Box>
    );
  }

  const items = sub.subscriptionItems ?? [];

  return (
    <Box sx={detailPageContainerSx}>
      <Box sx={{ ...detailHeaderRowWrapSx, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={handleBack} sx={detailBackButtonSx}>
          {t('common.back')}
        </Button>
        <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 200 } }}>
          <Typography variant="h4" component="h1" sx={detailTitleSx}>
            {t('subscriptions.subscriptionLabel')} {sub.number}
          </Typography>
        </Box>
        <Chip
          label={translateSubscriptionState(t, sub.state)}
          color={subscriptionStateColors[sub.state] || 'default'}
          size="small"
        />
        {fromTenantId && (
          <Button size="small" onClick={() => navigate(`/customers/${fromTenantId}`)}>
            {t('common.toCustomer')}
          </Button>
        )}
      </Box>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, width: '100%', boxSizing: 'border-box' }}
      >
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {t('organizationDetail.masterData')}
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('subscriptions.subNumber')}
            </Typography>
            <Typography variant="body2">{sub.number}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.status')}
            </Typography>
            <Typography variant="body2">{translateSubscriptionState(t, sub.state)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('tenantDetail.tabsOrgUnits')}
            </Typography>
            <Typography variant="body2">{sub.organizationUnit?.name ?? '–'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.interval')}
            </Typography>
            <Typography variant="body2">
              {sub.intervalMonths ? `${sub.intervalMonths} ${t('subscriptions.months')}` : '–'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.created')}
            </Typography>
            <Typography variant="body2">{formatDate(sub.createdAt)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.updated')}
            </Typography>
            <Typography variant="body2">{formatDate(sub.updatedAt)}</Typography>
          </Box>
        </Box>
      </Paper>

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, width: '100%', boxSizing: 'border-box' }}
      >
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {t('subscriptions.amountsAndTerm')}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('subscriptions.amountGross')}
            </Typography>
            <Typography variant="body1">
              {formatCurrency(sub.amountGross, sub.currencyCode)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('subscriptions.amountNet')}
            </Typography>
            <Typography variant="body1">
              {formatCurrency(sub.amountNet, sub.currencyCode)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('subscriptions.activatedAt')}
            </Typography>
            <Typography variant="body2">{formatDate(sub.activationDate)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('subscriptions.cancellationAt')}
            </Typography>
            <Typography variant="body2">{formatDate(sub.cancellationDate)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('subscriptions.expiryAt')}
            </Typography>
            <Typography variant="body2">{formatDate(sub.expiryDate)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('subscriptions.termFrom')}
            </Typography>
            <Typography variant="body2">{formatDate(sub.currentTermStartDate)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t('subscriptions.termTo')}
            </Typography>
            <Typography variant="body2">{formatDate(sub.currentTermEndDate)}</Typography>
          </Box>
        </Box>
      </Paper>

      {sub.paymentAccount && (
        <Paper
          variant="outlined"
          sx={{ p: { xs: 1.5, sm: 2 }, mb: 2, width: '100%', boxSizing: 'border-box' }}
        >
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {t('subscriptions.paymentAccount')}
          </Typography>
          <Typography variant="body2">
            {t('common.gateway')}: {sub.paymentAccount.paymentGateway?.name ?? '–'}
          </Typography>
          <Typography variant="body2">
            {t('common.paymentTerm')}: {sub.paymentAccount.paymentTerm?.name ?? '–'}
          </Typography>
        </Paper>
      )}

      <Paper
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, width: '100%', boxSizing: 'border-box' }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            {t('subscriptions.positions')}
          </Typography>
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            size="small"
            onClick={openAdd}
            disabled={!planOptions.length}
          >
            {t('subscriptions.addItem')}
          </Button>
        </Box>
        <ResponsiveRowTableCards
          columns={itemColumns}
          rows={items}
          getRowKey={(item) => item.id}
          minTableWidth={720}
          emptyContent={
            <Typography variant="body2" color="text.secondary">
              {t('common.noData')}
            </Typography>
          }
        />
      </Paper>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('subscriptions.addItem')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            select
            label={t('common.plan')}
            value={planId}
            onChange={(e) => setPlanId(e.target.value)}
            fullWidth
            required
          >
            {planOptions.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name} ({p.code})
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('common.quantity')}
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            type="number"
            fullWidth
          />
          <TextField
            label={t('common.net')}
            value={unitNet}
            onChange={(e) => setUnitNet(e.target.value)}
            fullWidth
          />
          <TextField
            label={t('common.gross')}
            value={unitGross}
            onChange={(e) => setUnitGross(e.target.value)}
            fullWidth
          />
          <TextField
            label={t('subscriptions.itemCurrency')}
            value={currencyCode}
            onChange={(e) => setCurrencyCode(e.target.value)}
            fullWidth
          />
        </DialogContent>
      </Dialog>

      <Dialog open={!!editItem} onClose={() => setEditItem(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t('subscriptions.editItem')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label={t('common.quantity')}
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            type="number"
            fullWidth
          />
          <TextField
            label={t('common.net')}
            value={unitNet}
            onChange={(e) => setUnitNet(e.target.value)}
            fullWidth
          />
          <TextField
            label={t('common.gross')}
            value={unitGross}
            onChange={(e) => setUnitGross(e.target.value)}
            fullWidth
          />
          <TextField
            label={t('subscriptions.itemCurrency')}
            value={currencyCode}
            onChange={(e) => setCurrencyCode(e.target.value)}
            fullWidth
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
